import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";

import {
  MAX_BUTTONS,
  MAX_BUTTON_TEXT_LENGTH,
  VALID_BUTTON_TYPES,
} from "./templateValidation";

const useStyles = makeStyles((theme) => ({
  section: {
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  buttonRow: {
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    background: theme.palette.background.default,
  },
  rowHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  rowGrow: { flex: 1 },
  fields: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1),
  },
  full: { gridColumn: "1 / -1" },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.78rem",
    marginTop: theme.spacing(0.5),
  },
}));

const TYPE_LABELS = {
  QUICK_REPLY: "Resposta rápida",
  URL: "URL",
  PHONE_NUMBER: "Telefone",
  COPY_CODE: "Copiar código",
};

const blank = () => ({
  type: "QUICK_REPLY",
  text: "",
  url: "",
  phone_number: "",
  example: "",
});

const ButtonsSection = ({ form, setForm, fieldErrors }) => {
  const classes = useStyles();
  const items = form.buttons?.items || [];

  const setEnabled = (enabled) =>
    setForm((prev) => ({
      ...prev,
      buttons: { ...prev.buttons, enabled, items: enabled ? prev.buttons.items : [] },
    }));

  const updateItem = (idx, patch) =>
    setForm((prev) => {
      const next = [...(prev.buttons.items || [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, buttons: { ...prev.buttons, items: next } };
    });

  const addItem = () =>
    setForm((prev) => {
      if ((prev.buttons.items || []).length >= MAX_BUTTONS) return prev;
      return {
        ...prev,
        buttons: {
          ...prev.buttons,
          enabled: true,
          items: [...(prev.buttons.items || []), blank()],
        },
      };
    });

  const removeItem = (idx) =>
    setForm((prev) => {
      const next = [...(prev.buttons.items || [])];
      next.splice(idx, 1);
      return { ...prev, buttons: { ...prev.buttons, items: next } };
    });

  const moveItem = (idx, dir) =>
    setForm((prev) => {
      const next = [...(prev.buttons.items || [])];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...prev, buttons: { ...prev.buttons, items: next } };
    });

  return (
    <Box className={classes.section}>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!form.buttons?.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            color="primary"
          />
        }
        label={<Typography className={classes.sectionTitle}>Botões (opcional, até {MAX_BUTTONS})</Typography>}
      />

      {form.buttons?.enabled && (
        <>
          {items.map((b, idx) => {
            const err = fieldErrors?.[idx];
            return (
              <Box key={idx} className={classes.buttonRow}>
                <Box className={classes.rowHeader}>
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    label="Tipo"
                    value={b.type}
                    onChange={(e) => updateItem(idx, { type: e.target.value })}
                  >
                    {VALID_BUTTON_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {TYPE_LABELS[t]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box className={classes.rowGrow} />
                  <IconButton
                    size="small"
                    onClick={() => moveItem(idx, -1)}
                    disabled={idx === 0}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveItem(idx, 1)}
                    disabled={idx === items.length - 1}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => removeItem(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box className={classes.fields}>
                  <TextField
                    label="Texto do botão"
                    value={b.text || ""}
                    onChange={(e) => updateItem(idx, { text: e.target.value })}
                    inputProps={{ maxLength: MAX_BUTTON_TEXT_LENGTH }}
                    fullWidth
                    size="small"
                    variant="outlined"
                    helperText={`${(b.text || "").length}/${MAX_BUTTON_TEXT_LENGTH}`}
                  />

                  {b.type === "URL" && (
                    <>
                      <TextField
                        label="URL"
                        placeholder="https://..."
                        value={b.url || ""}
                        onChange={(e) => updateItem(idx, { url: e.target.value })}
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                      {(b.url || "").includes("{{") && (
                        <TextField
                          className={classes.full}
                          label="Exemplo da URL (com variável preenchida)"
                          value={b.example || ""}
                          onChange={(e) => updateItem(idx, { example: e.target.value })}
                          fullWidth
                          size="small"
                          variant="outlined"
                          required
                          helperText="Necessário quando a URL contém {{1}}"
                        />
                      )}
                    </>
                  )}

                  {b.type === "PHONE_NUMBER" && (
                    <TextField
                      label="Telefone (E.164)"
                      placeholder="+5511999999999"
                      value={b.phone_number || ""}
                      onChange={(e) =>
                        updateItem(idx, { phone_number: e.target.value })
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                    />
                  )}

                  {b.type === "COPY_CODE" && (
                    <TextField
                      label="Código a ser copiado (example)"
                      value={b.example || ""}
                      onChange={(e) => updateItem(idx, { example: e.target.value })}
                      fullWidth
                      size="small"
                      variant="outlined"
                      required
                    />
                  )}
                </Box>

                {err && <Typography className={classes.errorText}>{err}</Typography>}
              </Box>
            );
          })}

          <Button
            type="button"
            startIcon={<AddIcon />}
            onClick={addItem}
            disabled={items.length >= MAX_BUTTONS}
            color="primary"
            variant="outlined"
            size="small"
            style={{ textTransform: "none" }}
          >
            Adicionar botão ({items.length}/{MAX_BUTTONS})
          </Button>
        </>
      )}
    </Box>
  );
};

export default ButtonsSection;
